/**
 * Request-shape schemas for the /payroll and /shifts route families.
 *
 * These used to live inline in apps/api/src/routes/payroll.ts, invisible to
 * the strictness sweep (strictness.test.ts). Moved here verbatim — shapes
 * are unchanged; only the export location moved.
 */
import { z } from 'zod';
import { RecurringDeductionId } from './payroll.js';
import {
  IsoDate,
  PayrollAdjustmentId,
  PayrollRunId,
  PayslipId,
  UserId,
  WorkEntryId,
} from './primitives.js';

export const EndOfServiceQuery = z.object({
  as_of: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
    .optional(),
});

// Route bodies apply `.optional()` at the schema site — an empty POST body
// is the normal clock-in/out case, not a validation error.
export const ShiftNoteBody = z.object({ note: z.string().max(280).optional() });
export const ListShiftsQuery = z.object({
  staff_user_id: UserId.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

/** `:staffUserId` path parameter (compensation history, leave). */
export const StaffUserIdParams = z.object({ staffUserId: UserId });

export const WorkEntryIdParams = z.object({ id: WorkEntryId });
export const WorkEntriesSummaryQuery = z.object({
  from: IsoDate,
  to: IsoDate,
  staff_user_id: UserId.optional(),
});

export const SetLeaveTermsBody = z
  .object({
    // 366 rather than 365: a leap year exists, and a cap that refuses
    // a legitimate value is worse than one nobody will ever reach.
    annual_leave_days: z.number().int().min(0).max(366).optional(),
    hire_date: IsoDate.optional(),
  })
  .refine((b) => b.annual_leave_days !== undefined || b.hire_date !== undefined, {
    message: 'Nothing to change.',
  });

export const ListAdjustmentsQuery = z.object({
  staff_user_id: UserId.optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
export const PayrollAdjustmentIdParams = z.object({ id: PayrollAdjustmentId });

/** Shared "filter by staff member" query (recurring deductions, payslips). */
export const StaffUserIdFilterQuery = z.object({ staff_user_id: UserId.optional() });
export const RecurringDeductionIdParams = z.object({ id: RecurringDeductionId });

export const PayrollRunIdParams = z.object({ id: PayrollRunId });
export const PayslipIdParams = z.object({ id: PayslipId });

/**
 * `:id` for PATCH /payroll/loans/:id/repayment-plan. Deliberately looser than
 * money-routes' LoanIdParams (any non-empty string, not a checked LoanId) —
 * moved verbatim; the service layer 404s unknown ids either way.
 */
export const LoanRepaymentPlanParams = z.object({ id: z.string().min(1) });
